import React from 'react';
import { Navigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAuth, canAccessPage, hasGovernancePermission, isPlatformAdmin, SystemRole } from '@/shared/auth/unified-auth';
import { Text } from '@agion/ui';
import { NavigationGate, type NavigationPage } from '@/shared/auth/navigation-gate';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: SystemRole[];
  requiredPermission?: 'read' | 'write' | 'admin';
  requiredPage?: string;
  /** When true, only Agion staff email domains can access this route */
  requirePlatformAdmin?: boolean;
  /**
   * The navigation permission of the page's sidebar entry (agion/Agion#2556):
   * a role without it is sent to /unauthorized, as the sidebar hides it.
   */
  requiredNavigation?: NavigationPage;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requiredPermission,
  requiredPage,
  requirePlatformAdmin,
  requiredNavigation,
}) => {
  const { t } = useTranslation(['common']);
  const { user, isAuthenticated, isLoading } = useAuth();

  // SSO redirect is handled by App.tsx's <Unauthenticated> + SSORedirect.
  // ProtectedRoute only checks role/permission/page within an already-authenticated context.

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Show loading spinner while SSO redirect is happening
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <Text color="secondary" className="dark:text-slate-400">{t('common:auth.redirectingToLogin', 'Redirecting to login...')}</Text>
        </div>
      </div>
    );
  }

  // Check platform admin requirement (email domain gate)
  if (requirePlatformAdmin && user && !isPlatformAdmin(user)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Check role requirement
  if (requiredRole && user && !requiredRole.includes(user.role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Check permission requirement
  if (requiredPermission && user && !hasGovernancePermission(user.role, requiredPermission)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Check page access
  if (requiredPage && user && !canAccessPage(user.role, requiredPage)) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (requiredNavigation) {
    return <NavigationGate page={requiredNavigation}>{children}</NavigationGate>;
  }

  return <>{children}</>;
};
